import { create } from "zustand";
import { api } from "../utils/api";
import type { User } from "../types";

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  _checked: boolean;

  checkAuth: (force?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  exitImpersonation: () => Promise<void>;
  updatePreferences: (patch: { units?: "standard" | "metric" }) => Promise<void>;
  loginWithIracing: (opts?: { inviteCode?: string; returnTo?: string }) => void;
  login: (email: string, password: string, desktopState?: string) => Promise<void>;
  authorizeDesktop: (desktopState: string) => Promise<void>;
  register: (inviteCode: string, iracingCustId: string, displayName: string, email: string, password: string) => Promise<void>;
  registerTeam: (email: string, password: string, teamName: string, displayName?: string) => Promise<void>;
  registerIndividual: (email: string, password: string, displayName?: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: false,
  error: null,
  _checked: false,

  checkAuth: async (force = false) => {
    if (get()._checked && !force) return;
    set({ loading: true, error: null });
    try {
      const data = await api<User>("/api/auth/me");
      set({ user: data, loading: false, _checked: true });
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      if (apiErr.status === 401) {
        try {
          await api("/api/auth/refresh", { method: "POST" });
          const data = await api<User>("/api/auth/me");
          set({ user: data, loading: false, _checked: true });
          return;
        } catch {
          set({ user: null, loading: false, _checked: true });
        }
      } else {
        set({ error: apiErr.message || "Auth check failed", user: null, loading: false, _checked: true });
      }
    }
  },

  // End an admin "view as team" support session. Errors are ignored — the grant
  // is short-lived server-side either way; the caller hard-navigates after this.
  exitImpersonation: async () => {
    try {
      await api("/api/admin/impersonation/exit", { method: "POST" });
    } catch { /* ignore */ }
    set((state) => ({
      user: state.user ? { ...state.user, viewingAs: null } : state.user,
    }));
  },

  logout: async () => {
    try {
      await api("/api/auth/logout", { method: "POST" });
    } catch { /* ignore */ }
    set({ user: null, error: null, loading: false, _checked: true });
    window.location.href = "/login";
  },

  updatePreferences: async (patch) => {
    if (!get().user) return;
    set({ loading: true, error: null });
    try {
      const result = await api<{ preferences: User["preferences"] }>("/api/auth/preferences", {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      set((state) => ({
        user: state.user
          ? {
              ...state.user,
              preferences: result.preferences,
            }
          : state.user,
        loading: false,
        _checked: true,
      }));
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      set({ error: apiErr.message || "Failed to save preferences", loading: false });
      throw err;
    }
  },

  login: async (email: string, password: string, desktopState?: string) => {
    set({ loading: true, error: null });
    try {
      await api("/api/auth/login-simple", {
        method: "POST",
        body: JSON.stringify({ email, password, desktopState }),
      });
      const data = await api<User>("/api/auth/me");
      set({ user: data, loading: false, _checked: true });
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      set({ error: apiErr.message || "Login failed", loading: false });
      throw err;
    }
  },

  // Desktop "Sign in with web" instant path: an already-signed-in web user
  // hands a session to the waiting agent (no password re-entry).
  authorizeDesktop: async (desktopState: string) => {
    await api("/api/auth/desktop/authorize", {
      method: "POST",
      body: JSON.stringify({ desktopState }),
    });
  },

  register: async (inviteCode: string, iracingCustId: string, displayName: string, email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      await api("/api/auth/register", {
        method: "POST",
        body: JSON.stringify({ inviteCode, iracingCustId, displayName, email, password }),
      });
      const data = await api<User>("/api/auth/me");
      set({ user: data, loading: false, _checked: true });
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      set({ error: apiErr.message || "Registration failed", loading: false });
      throw err;
    }
  },

  // Self-serve team signup (#253A/#254): create a team, free in early access,
  // no invite code, no iRacing customer ID.
  registerTeam: async (email: string, password: string, teamName: string, displayName?: string) => {
    set({ loading: true, error: null });
    try {
      await api("/api/auth/register-team", {
        method: "POST",
        body: JSON.stringify({ email, password, teamName, displayName }),
      });
      const data = await api<User>("/api/auth/me");
      set({ user: data, loading: false, _checked: true });
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      set({ error: apiErr.message || "Sign-up failed", loading: false });
      throw err;
    }
  },

  // Solo driver signup (SimSense merge Phase 1): free individual account,
  // backend creates a hidden comped personal team behind the scenes.
  registerIndividual: async (email: string, password: string, displayName?: string) => {
    set({ loading: true, error: null });
    try {
      await api("/api/auth/register-individual", {
        method: "POST",
        body: JSON.stringify({ email, password, displayName }),
      });
      const data = await api<User>("/api/auth/me");
      set({ user: data, loading: false, _checked: true });
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      set({ error: apiErr.message || "Sign-up failed", loading: false });
      throw err;
    }
  },

  loginWithIracing: (opts) => {
    set({ loading: true, error: null });
    const params = new URLSearchParams();
    const inviteCode = opts?.inviteCode?.trim();
    const returnTo = opts?.returnTo?.trim();
    if (inviteCode) params.set("invite_code", inviteCode);
    if (returnTo) params.set("return_to", returnTo);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    window.location.href = `/api/auth/iracing/login${suffix}`;
  },
}));
